import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/auth";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import { Login, Register } from "@/pages/AuthPages";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Players from "@/pages/Players";
import Finance from "@/pages/Finance";
import Schedule from "@/pages/Schedule";
import Attendance from "@/pages/Attendance";
import Inquiries from "@/pages/Inquiries";
import News from "@/pages/News";
import Gallery from "@/pages/Gallery";
import Directory from "@/pages/Directory";
import Profile from "@/pages/Profile";
import Announcements from "@/pages/Announcements";
import Settings from "@/pages/Settings";
import { PaymentSuccess, PaymentCancel, PaymentResult } from "@/pages/Payments";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#165A2E] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/payment-result" element={<PaymentResult />} />
      <Route path="/app" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="players" element={<Players />} />
        <Route path="finance" element={<Finance />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="attendance" element={<Protected roles={["admin", "staff"]}><Attendance /></Protected>} />
        <Route path="inquiries" element={<Inquiries />} />
        <Route path="news" element={<News />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="directory" element={<Directory />} />
        <Route path="profile" element={<Profile />} />
        <Route path="announcements" element={<Protected roles={["admin", "staff"]}><Announcements /></Protected>} />
        <Route path="settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}
